import * as React from "react";
import { cn } from "../../lib/utils";
import { Text } from "../Text";
import { IconButton } from "../IconButton";
import { Divider } from "../Divider";

export interface BottomSheetProps {
  /**
   * Whether the bottom sheet is open
   */
  isOpen: boolean;
  /**
   * Callback when the bottom sheet should close
   */
  onClose?: () => void;
  /**
   * Bottom sheet title - optional
   */
  title?: string;
  /**
   * Bottom sheet description/subtitle - optional
   */
  description?: string;
  /**
   * Bottom sheet footer content - optional
   */
  footer?: React.ReactNode;
  /**
   * Bottom sheet body content
   */
  children: React.ReactNode;
  /**
   * Style variant - "default" applies default styles, "unstyled" removes all default styling
   */
  variant?: "default" | "unstyled";
  /**
   * Whether to show the close button in header
   */
  showCloseButton?: boolean;
  /**
   * Whether to show the drag handle
   */
  showDragHandle?: boolean;
  /**
   * Whether clicking the overlay closes the bottom sheet
   */
  closeOnOverlayClick?: boolean;
  /**
   * Whether pressing Escape closes the bottom sheet
   */
  closeOnEscape?: boolean;
  /**
   * Whether swiping down closes the bottom sheet
   */
  closeOnSwipeDown?: boolean;
  /**
   * Maximum height of the bottom sheet (percentage or specific value)
   */
  maxHeight?: string;
  /**
   * Custom class name for the bottom sheet container
   */
  className?: string;
  /**
   * Custom class name for the bottom sheet content
   */
  contentClassName?: string;
  /**
   * Custom class name for the header
   */
  headerClassName?: string;
  /**
   * Custom class name for the body
   */
  bodyClassName?: string;
  /**
   * Custom class name for the footer
   */
  footerClassName?: string;
  /**
   * Custom class name for the overlay
   */
  overlayClassName?: string;
  /**
   * ARIA label for accessibility
   */
  ariaLabel?: string;
  /**
   * ARIA description for accessibility
   */
  ariaDescribedBy?: string;
}

const BottomSheet = React.forwardRef<HTMLDivElement, BottomSheetProps>(
  (
    {
      isOpen,
      onClose,
      title,
      description,
      footer,
      children,
      variant = "default",
      showCloseButton = true,
      showDragHandle = true,
      closeOnOverlayClick = true,
      closeOnEscape = true,
      closeOnSwipeDown = true,
      maxHeight = "90vh",
      className,
      contentClassName,
      headerClassName,
      bodyClassName,
      footerClassName,
      overlayClassName,
      ariaLabel,
      ariaDescribedBy,
    },
    ref
  ) => {
    const sheetRef = React.useRef<HTMLDivElement>(null);
    const contentRef = ref || sheetRef;
    const [isClosing, setIsClosing] = React.useState(false);
    const [touchStart, setTouchStart] = React.useState<number | null>(null);
    const [touchEnd, setTouchEnd] = React.useState<number | null>(null);

    // Handle escape key
    React.useEffect(() => {
      if (!isOpen || !closeOnEscape || !onClose) return;

      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === "Escape") {
          handleClose();
        }
      };

      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    }, [isOpen, closeOnEscape, onClose]);

    // Prevent body scroll when bottom sheet is open
    React.useEffect(() => {
      if (isOpen) {
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "";
      }

      return () => {
        document.body.style.overflow = "";
      };
    }, [isOpen]);

    // Reset closing state when opening
    React.useEffect(() => {
      if (isOpen) {
        setIsClosing(false);
      }
    }, [isOpen]);

    // Handle close with animation
    const handleClose = () => {
      if (!onClose) return;
      setIsClosing(true);
      // Wait for animation to complete before calling onClose
      setTimeout(() => {
        onClose();
        setIsClosing(false);
      }, 300); // Match animation duration
    };

    // Handle overlay click
    const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
      if (closeOnOverlayClick && e.target === e.currentTarget) {
        handleClose();
      }
    };

    // Handle touch events for swipe down
    const handleTouchStart = (e: React.TouchEvent) => {
      if (!closeOnSwipeDown) return;
      setTouchEnd(null);
      setTouchStart(e.targetTouches[0].clientY);
    };

    const handleTouchMove = (e: React.TouchEvent) => {
      if (!closeOnSwipeDown) return;
      setTouchEnd(e.targetTouches[0].clientY);
    };

    const handleTouchEnd = () => {
      if (!closeOnSwipeDown || !touchStart || !touchEnd) return;

      const distance = touchEnd - touchStart;
      const isSwipeDown = distance > 100; // Minimum swipe distance

      if (isSwipeDown) {
        handleClose();
      }

      setTouchStart(null);
      setTouchEnd(null);
    };

    // Don't render if not open and not closing
    if (!isOpen && !isClosing) return null;

    const hasHeader = title || description;

    return (
      <div
        className={cn(
          "fixed inset-0 z-9999 flex items-end justify-center",
          className
        )}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel || title}
        aria-describedby={ariaDescribedBy}
      >
        {/* Overlay */}
        <div
          className={cn(
            "absolute inset-0 z-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300",
            isClosing ? "opacity-0" : "opacity-100",
            overlayClassName
          )}
          onClick={handleOverlayClick}
          aria-hidden="true"
        />

        {/* Bottom Sheet Content */}
        <div
          ref={contentRef}
          className={cn(
            "relative z-10 w-full transition-transform duration-300",
            "flex flex-col overflow-hidden",
            variant === "default" && "bg-white rounded-t-2xl shadow-xl",
            isClosing ? "animate-slide-out-bottom" : "animate-slide-in-bottom",
            contentClassName
          )}
          style={{ maxHeight }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Drag Handle */}
          {showDragHandle && variant === "default" && (
            <div className="flex justify-center pt-3 pb-2">
              <div className="w-10 h-1 bg-neutral-300 rounded-full" />
            </div>
          )}

          {/* Header */}
          {hasHeader && (
            <div
              className={cn(
                "flex items-start justify-between gap-4",
                variant === "default" && "px-6",
                variant === "default" && !showDragHandle && "pt-6",
                variant === "default" && showDragHandle && "pt-2",
                variant === "default" && !description && "pb-4",
                variant === "default" && description && "pb-2",
                headerClassName
              )}
            >
              <div className="flex-1">
                {title && (
                  <Text
                    as="h2"
                    variant="body"
                    size="large"
                    weight="semibold"
                    color="default"
                  >
                    {title}
                  </Text>
                )}
                {description && (
                  <Text
                    as="p"
                    variant="body"
                    size="small"
                    weight="regular"
                    color="subtle"
                    className="mt-1"
                  >
                    {description}
                  </Text>
                )}
              </div>

              {showCloseButton && onClose && (
                <IconButton
                  icon="close"
                  onClick={handleClose}
                  color="neutral"
                  size="small"
                  aria-label="Close bottom sheet"
                  className="shrink-0"
                />
              )}
            </div>
          )}

          {/* Header with close button only (no title/description) */}
          {!hasHeader && showCloseButton && onClose && (
            <div
              className={cn(
                "absolute z-10",
                variant === "default" && "top-4 right-4"
              )}
            >
              <IconButton
                icon="close"
                onClick={handleClose}
                color="neutral"
                size="small"
                aria-label="Close bottom sheet"
              />
            </div>
          )}

          {/* Body */}
          <div
            className={cn(
              "flex-1 overflow-y-auto",
              variant === "default" && "px-6",
              variant === "default" && hasHeader && "py-4",
              variant === "default" && !hasHeader && !showDragHandle && "pt-6 pb-4",
              variant === "default" && !hasHeader && showDragHandle && "pt-2 pb-4",
              variant === "default" && !footer && "pb-6",
              bodyClassName
            )}
          >
            {children}
          </div>

          {/* Footer */}
          {footer && (
            <div className="flex flex-col">
              {variant === "default" && (
                <Divider thickness="thin" variant="muted" />
              )}
              <div
                className={cn(
                  "flex items-center justify-end gap-3",
                  variant === "default" && "px-6 py-4",
                  footerClassName
                )}
              >
                {footer}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
);

BottomSheet.displayName = "BottomSheet";

export { BottomSheet };
